import React, { useEffect, useState } from "react";
import { Car } from "../Types/Car";
import CarCard from "./CarCard";
import { getPublicCarsInStockPaginated } from "../../api/cars";
import useInfiniteScrollPagination from "../../hooks/InfiniteScrollPagination";
import "./CarList.css";

const PAGE_SIZE = 12;

const CarList: React.FC = () => {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  // Bumped by the retry button to re-run the fetch for the current page.
  const [retryToken, setRetryToken] = useState<number>(0);

  // Gate auto-advance on both loading and error: after a failed page we hold at
  // currentPage (no skipping) until the user retries.
  const { currentPage, pageSize, setTotalPages } = useInfiniteScrollPagination({
    pageSize: PAGE_SIZE,
    loading: loading || error,
  });

  useEffect(() => {
    // Guard against a page's response landing after the component unmounts (or
    // after StrictMode's double-invoke) writing stale state.
    let cancelled = false;
    const fetchPage = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getPublicCarsInStockPaginated(currentPage, pageSize);
        if (cancelled) return;
        setCars((prev) =>
          currentPage === 1 ? res.data : [...prev, ...res.data],
        );
        setTotalPages(res.totalPages);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching cars", err);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPage();
    return () => {
      cancelled = true;
    };
  }, [currentPage, pageSize, setTotalPages, retryToken]);

  const retry = () => setRetryToken((n) => n + 1);

  // Only the first load shows the full-page spinner; later pages append below.
  if (loading && cars.length === 0) {
    return (
      <div className="car-list loading-car-list">
        <div className="loading"></div>
      </div>
    );
  }
  if (error && cars.length === 0) {
    return (
      <div className="car-list">
        <div className="no-cars-text">
          Autojen lataus epäonnistui. Yritä hetken kuluttua uudelleen.
        </div>
        <button className="car-list-retry" onClick={retry}>
          Yritä uudelleen
        </button>
      </div>
    );
  }
  if (!loading && !error && cars.length === 0) {
    return (
      <div className="car-list">
        <div className="no-cars-text">Ei autoja saatavilla</div>
      </div>
    );
  }
  return (
    <>
      <div className="car-list">
        {cars.map((car) => (
          <CarCard key={car.ID} car={car} />
        ))}
      </div>
      {loading && cars.length > 0 && (
        <div className="car-list-loading-more">
          <div className="loading"></div>
        </div>
      )}
      {error && cars.length > 0 && (
        <div className="car-list-loading-more">
          <button className="car-list-retry" onClick={retry}>
            Lisää autoja ei voitu ladata. Yritä uudelleen
          </button>
        </div>
      )}
    </>
  );
};
export default CarList;
